"use client";

import { useEffect, useState } from "react";

/**
 * EPIC-028 STORY-1601 — region + locale resolved from cookie.
 *
 * The cookie `granbay.region` is set by the server-side layout
 * boundary on first visit (reads CF-IPCountry header). This client
 * helper reads the same cookie and exposes the resolved region for
 * locale-aware UI bits.
 *
 * Authenticated users override via `users.locale` (handled at the
 * app/layout boundary too — the cookie is updated when the user
 * logs in / changes their locale pref).
 */

// Locked product vision (2026-05-01) — Phase 1 markets are
// BR + MX + CA (excluding Ontario) + IN, plus the global default.
// 'global' covers US + everywhere else not in the list.
export type RegionCode =
  | "brazil"
  | "mexico"
  | "canada"
  | "india"
  | "global";

// All locales the i18n catalogs support today. en-IN remains pending
// (deferred as post-launch in EPIC-030); India users fall through to
// 'en' for the chrome strings while showing the Indian flag in the
// picker so they still feel "addressed" by the surface.
export type SupportedLocale =
  | "en"
  | "pt"
  | "en-CA"
  | "fr-CA"
  | "es-MX"
  | "hi";

export interface RegionState {
  region: RegionCode;
  locale: SupportedLocale;
  // Currency stays USDC-as-truth per EPIC-029 STORY-1716 — display-
  // currency projection (BRL/MXN/CAD/INR) lives in lib/format and
  // user prefs (`users.display_currency`), not in the region cookie.
  // BRL/USD legacy values still parse for backwards compat; MXN/CAD/
  // INR added 2026-05-21 alongside the region-immersion extension so
  // the cookie can carry the matching primary fiat for MX/CA/IN flags.
  currency: "BRL" | "USD" | "USDC" | "MXN" | "CAD" | "INR";
  // Detected via IP — false if the user explicitly set their locale
  // (cookie was written by the server with `detected=false`).
  detected: boolean;
  // ISO country from CF-IPCountry. NULL when not detected (dev /
  // test / VPN / unsupported edge).
  countryCode: string | null;
}

const VALID_REGIONS: RegionCode[] = [
  "brazil",
  "mexico",
  "canada",
  "india",
  "global",
];
const VALID_LOCALES: SupportedLocale[] = [
  "en",
  "pt",
  "en-CA",
  "fr-CA",
  "es-MX",
  "hi",
];

const DEFAULT: RegionState = {
  region: "global",
  locale: "en",
  currency: "USD",
  detected: false,
  countryCode: null,
};

function parseRegion(v: unknown): RegionCode {
  return VALID_REGIONS.includes(v as RegionCode)
    ? (v as RegionCode)
    : "global";
}

function parseLocale(v: unknown): SupportedLocale {
  return VALID_LOCALES.includes(v as SupportedLocale)
    ? (v as SupportedLocale)
    : "en";
}

function parseCurrency(
  v: unknown,
): "BRL" | "USD" | "USDC" | "MXN" | "CAD" | "INR" {
  if (
    v === "BRL" ||
    v === "USD" ||
    v === "USDC" ||
    v === "MXN" ||
    v === "CAD" ||
    v === "INR"
  ) {
    return v;
  }
  return "USD";
}

export function readRegionCookie(): RegionState {
  if (typeof document === "undefined") return DEFAULT;
  try {
    const raw = document.cookie
      .split("; ")
      .find((c) => c.startsWith("granbay.region="));
    if (!raw) return DEFAULT;
    const decoded = decodeURIComponent(raw.split("=")[1] || "");
    const parsed = JSON.parse(decoded) as Partial<RegionState>;
    return {
      region: parseRegion(parsed.region),
      locale: parseLocale(parsed.locale),
      currency: parseCurrency(parsed.currency),
      detected: parsed.detected === true,
      countryCode: parsed.countryCode ?? null,
    };
  } catch {
    return DEFAULT;
  }
}

/**
 * Hook variant — re-renders on cookie change. Closed-beta scope:
 * polls on mount + visibility change. Cookie observers (a future
 * web standard) would be the right tool but it's not yet shipped.
 */
export function useRegion(): RegionState {
  const [state, setState] = useState<RegionState>(DEFAULT);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
    setState(readRegionCookie());
    const onVis = () => setState(readRegionCookie());
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  return mounted ? state : DEFAULT;
}

/**
 * Imperative writer — updates the cookie + triggers a re-read on
 * the same tab. Used by the locale-mismatch banner's "switch" action
 * and by the future locale picker (STORY-1604).
 */
export function writeRegionCookie(state: RegionState): void {
  if (typeof document === "undefined") return;
  const value = encodeURIComponent(JSON.stringify(state));
  // 30-day expiry per the spec.
  const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `granbay.region=${value}; path=/; expires=${expires}; samesite=lax`;
}
